import type { StateCreator } from 'zustand';
import type { StoreType, CalcSlice } from '@/types/store';
import { computeStudy } from '@bioavail/shared-logic/compute';
import type { AuditEntry } from '@bioavail/shared-types';
import { uid } from '@/data/factory';

export const createCalcSlice: StateCreator<StoreType, [], [], CalcSlice> = (set, get) => ({
  output: null,
  isComputing: false,
  lastError: null,

  compute: () => {
    const { studies, activeStudyId } = get();
    const active = studies.find((s) => s.id === activeStudyId);
    if (!active) {
      set({ lastError: 'No active study to compute.' });
      return;
    }

    set({ isComputing: true });
    try {
      const computed = computeStudy(active);
      const output = { ...computed, computedAt: Date.now() };
      set({ output, isComputing: false, lastError: null });

      // Append audit entry (Phase 2) — debounced at most every 5s.
      appendAudit(get, set, active, output.bioavailability, output.warnings);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Computation failed.';
      set({ isComputing: false, lastError: message });
      // Surface the error as a danger toast (H1).
      get().pushToast(message, 'danger');
    }
  },
});

let lastAuditTime = 0;
function appendAudit(
  get: () => StoreType,
  set: (partial: Partial<StoreType>) => void,
  study: Parameters<typeof computeStudy>[0],
  result: import('@/types/domain').BioavailabilityResult,
  warnings: import('@/types/domain').ValidationWarning[],
): void {
  const now = Date.now();
  if (now - lastAuditTime < 5000) return;
  lastAuditTime = now;

  const entry: AuditEntry = {
    id: uid('audit'),
    timestamp: new Date().toISOString(),
    action: 'computeF',
    studySnapshot: study,
    resultSnapshot: result,
    warnings,
  };
  const trail = [...get().trail, entry];
  // Prune beyond 500 entries.
  const pruned = trail.length > 500 ? trail.slice(trail.length - 500) : trail;
  set({ trail: pruned });
}
